import { useState } from 'react';
import { Room } from '../types/user';
import { useUser } from '../context/UserContext';
import { ShareRoomModal } from './ShareRoomModal';
import './ShareRoomButton.css';

interface ShareRoomButtonProps {
  room: Room;
  variant?: 'icon' | 'button';
  className?: string;
  /** Optional label override for the text/button variant. */
  label?: string;
}

/**
 * Trigger for the room invite panel. `icon` (dashboard cards) shows a 🔗 pill;
 * `button` (in-room header) shows a labelled "Invite" button. Both open the rich
 * ShareRoomModal (note editor + WhatsApp/Email/Copy/native share). The recipient's
 * WhatsApp preview shows the room thumbnail via the server-rendered OG shim.
 */
export function ShareRoomButton({ room, variant = 'icon', className = '', label }: ShareRoomButtonProps) {
  const { user } = useUser();
  const [open, setOpen] = useState(false);

  if (!room.code) {
    return null;
  }

  const openModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <div className={`share-room-container ${className}`}>
      {variant === 'icon' ? (
        <button
          className="share-room-btn icon-btn"
          onClick={openModal}
          title="Invite friends"
          aria-label="Invite friends to this room"
        >
          <span className="share-icon">&#x1F517;</span>
        </button>
      ) : (
        <button className="share-room-btn text-btn" onClick={openModal}>
          <span className="share-icon">&#x1F517;</span>
          {label ?? 'Invite'}
        </button>
      )}

      {open && (
        <ShareRoomModal
          room={room}
          inviterName={user?.displayName}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
